<script lang="ts" setup>
import { Modal, UserAvatar, Dropdown } from "@/components";
import {
  DocumentSpace,
  DocumentSpaceAccess,
  useUpdateAccessMutation,
  useRemoveAccessMutation,
} from "@/graphql/operations";
import { PropType, ref, computed, reactive, nextTick } from "vue";
import { useStore } from "vuex";
import AccessUserSearch from "./AccessUserSearch.vue";
import CollaboratorRow from "./CollaboratorRow.vue";
import TransferOwnershipModal from "./TransferOwnershipModal.vue";

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  visibleUserCount: {
    type: Number,
    required: true,
  },
  visibleUserLimit: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const emits = defineEmits(["close", "refreshData", "changeState"]);
const emitClose = () => emits("close");

const myAccess = computed(() =>
  props.documentSpace.documentSpaceAccesses.nodes.find(
    (a) => a?.user?.id === store.getters.user.postgresId,
  ),
);

const userAccess = computed(() =>
  props.documentSpace.documentSpaceAccesses.nodes.filter(
    (a) => a?.user?.id !== store.getters.user.postgresId,
  ),
);

const transferVisibilityError = ref(false);
const transferOwnershipModalActive = ref<boolean>(false);
const newOwner = ref<DocumentSpaceAccess>();
const transferOwnershipOpener = ref<HTMLElement>();

const { executeMutation: updateVisibility } = useUpdateAccessMutation();
const { executeMutation: executeRemoveAccess } = useRemoveAccessMutation();
const updateAccess = async (
  action: string,
  access: DocumentSpaceAccess,
  opener?: HTMLElement,
) => {
  // remove access
  if (action === "remove") {
    if (access.role === "owner") {
      return;
    }

    const result = await executeRemoveAccess({ id: access.id });
    if (result.error) {
      //TODO handle error;
    }
  } else if (action === "transfer_ownership") {
    if (myAccess.value!.role !== "owner") {
      return;
    }

    transferVisibilityError.value = false;
    transferOwnershipModalActive.value = true;
    newOwner.value = access;
    transferOwnershipOpener.value = opener;
  } else {
    // update visibility
    const displayContactDetails = action === "visible";
    if (displayContactDetails === access.displayContactDetails) {
      return;
    }

    const result = await updateVisibility({
      id: access.id,
      displayContactDetails: displayContactDetails,
    });

    if (result.error) {
      //TODO handle error;
    }
  }

  //display flash message?
  emits("refreshData");
};

const closeTransferOwnershipModal = () => {
  transferOwnershipModalActive.value = false;
  void nextTick(() => transferOwnershipOpener.value?.focus());
};

const searchActive = ref<boolean>(false);
const handleSearchActive = (isActive: boolean) => {
  if (isActive) {
    emits("changeState");
  }
};

const userSearch = ref<InstanceType<typeof AccessUserSearch>>();
defineExpose({
  focusUserSearchActivation: () => userSearch.value?.focusActivation(),
});
</script>

<template>
  <modal :active="true" @close="emitClose" :scrolling="true" class="medium">
    <template #header> Access </template>

    <template #body>
      <p v-if="transferVisibilityError" role="alert" class="save_error">
        {{ $t("documentSpace.access.transferOwnership.visibilityFailed") }}
      </p>
      <access-user-search
        ref="userSearch"
        :documentSpace="documentSpace"
        @refreshData="emits('refreshData')"
        :active="searchActive"
        @stateChange="handleSearchActive"
      />

      <h3>
        {{ $t("documentSpace.access.title") }}
      </h3>

      <div>
        <collaborator-row
          v-if="myAccess"
          :access="myAccess"
          :isCurrentUser="true"
          :visibleUserCount="visibleUserCount"
          :visibleUserLimit="visibleUserLimit"
          :isOwner="myAccess.role === 'owner'"
          @update="updateAccess"
        />

        <collaborator-row
          v-for="access in userAccess"
          :key="access!.id"
          :access="access!"
          :isCurrentUser="false"
          :visibleUserCount="visibleUserCount"
          :visibleUserLimit="visibleUserLimit"
          :isOwner="myAccess!.role === 'owner'"
          @update="updateAccess"
        />
      </div>

      <transfer-ownership-modal
        v-if="transferOwnershipModalActive && newOwner"
        :newOwner="newOwner"
        :visibleUserCount="visibleUserCount"
        :visibleUserLimit="visibleUserLimit"
        @visibilityError="transferVisibilityError = true"
        @close="closeTransferOwnershipModal"
        @refreshData="emits('refreshData')"
      />
    </template>
  </modal>
</template>

<style scoped lang="scss">
.save_error {
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}

h3 {
  font-weight: 500;
  font-size: 1.1em;
  margin-top: 1.2em;
}
</style>
